import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { TooltipComponent } from '../../../../shared/components/ui/tooltip/tooltip.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MicroServiceHttpService } from '../../../../core/services/microServiceHttp.service';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  ChatWorkspaceBotContext,
  ChatWorkspaceInterfaceComponent,
} from '../../../../shared/components/chat-workspace-interface/chat-workspace-interface.component';
import { toast } from 'ngx-sonner';
import { ApiService } from '../../../../core/services/api.service';

export interface Chatbot {
  pid: number;
  name: string;
  description?: string;
  productId: number;
  productName: string;
  userId: number;
  userName: string;
  chatbotSettingId: number;
  knowledgeBaseId: number | null;
  securityId: number;
  readyForDeployment: boolean | number;
  isDeployed: boolean | number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}



@Component({
  selector: 'app-chatbot-list',
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponentV2,
    TooltipComponent,
    MatDialogModule,
    ChatWorkspaceInterfaceComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './chatbot-list.component.html',
})
export class ChatbotListComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private microServiceHttpService = inject(MicroServiceHttpService);
  private dialog = inject(MatDialog);
  private readonly apiService = inject(ApiService);

  /** Matches truncated preview length in list cards; keep in sync with template. */
  readonly descriptionPreviewMax = 50;

  chatbots = signal<(Chatbot & { initials: string; descriptionPartial?: string })[]>([]);
  chatbotsCount = signal<number>(0);
  viewMore = signal<boolean>(false);
  viewMoreChatbotId = signal<number | null>(null);

  /** Expanded card ids; single expand uses one id, expand-all uses every bot pid. */
  expandedChatbotIds = signal<number[]>([]);



  /** Bottom-right workspace chat panel (shell only; messaging wired later). */
  chatPanelOpen = signal(false);
  chatPanelBot = signal<ChatWorkspaceBotContext | null>(null);

  allChatbotsExpanded = computed(() => {
    const bots = this.chatbots();
    const ids = new Set(this.expandedChatbotIds());
    if (bots.length === 0) return false;
    return bots.every((b) => ids.has(b.pid));
  });

  /**
   * Subscribes to product selection and catalog so the list loads once after signals are ready
   * and refetches when either changes. `getChatbotsApi` must not also run from `ngOnInit` or the
   * same request fires twice on initial navigation.
   */
  constructor() {
    effect(() => {
      this.apiService.selectedProductId();
      this.apiService.products();
      this.getChatbotsApi();
    });
  }

  navigateToCreateBot(): void {
    this.router.navigate(['../bot'], {
      relativeTo: this.route,
      queryParams: { botId: 'new', step: 'setup' },
    });
  }

  navigateToBot(botId: number, step: string = 'agent'): void {
    this.router.navigate(['../bot'], {
      relativeTo: this.route,
      queryParams: { botId, step },
    });
  }

  expandChatbot(botPid: number): void {
    this.expandedChatbotIds.set([botPid]);
  }

  expandAllChatbots(): void {
    this.expandedChatbotIds.set(this.chatbots().map((b) => b.pid));
  }

  toggleExpandAll(): void {
    if (this.allChatbotsExpanded()) {
      this.expandedChatbotIds.set([]);
    } else {
      this.expandAllChatbots();
    }
  }

  /** Remove one card from expanded state, or pass nothing to collapse all. */
  minimizeChatbot(botPid?: number): void {
    if (botPid === undefined) {
      this.expandedChatbotIds.set([]);
      return;
    }
    this.expandedChatbotIds.update((ids) => ids.filter((id) => id !== botPid));
  }

  isChatbotExpanded(botPid: number): boolean {
    return this.expandedChatbotIds().includes(botPid);
  }



  onChatClick(bot: Chatbot): void {
    this.chatPanelBot.set({
      pid: bot.pid,
      name: bot.name,
      productName: bot.productName,
    });
    this.chatPanelOpen.set(true);
  }

  closeChatPanel(): void {
    this.chatPanelOpen.set(false);
    this.chatPanelBot.set(null);
  }

  initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  isTruthy(value: boolean | number | undefined): boolean {
    return value === true || value === 1;
  }

  private getChatbotsApi(): void {
    this.microServiceHttpService
      .get('whitelabel', 'chatbot/bot', {
        showToast: true,
        showLoader: true,
        successMessage: 'Chatbots fetched successfully',
        includeProductId: true,
      })
      .subscribe({
        next: (response: any) => {
          const list = (response?.data ?? []) as Chatbot[];
          this.chatbots.set(list.map((bot: any) => {
            return {
              ...bot,
              initials: this.initials(bot.name),
              descriptionPartial: bot.description
                ? bot.description.slice(0, this.descriptionPreviewMax) + '...'
                : bot.description,
            }
          }));
          this.chatbotsCount.set(list.length);
        },
      });
  }

  viewDescription(bot: Chatbot): void {
    if (!this.viewMore()) {
      this.viewMore.set(true);
      this.viewMoreChatbotId.set(bot.pid);
      setTimeout(() => {
        this.viewMore.set(false);
        this.viewMoreChatbotId.set(null);
      }, 5000);
    }
  }

  confirmDeleteChatbot(bot: Chatbot): void {
    const label = (bot.name ?? 'this chatbot').trim() || 'this chatbot';
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Delete chatbot?',
          description: `Are you sure you want to delete "${label}"? This action cannot be undone.`,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteChatbot(bot.pid);
        }
      });
  }

  private deleteChatbot(botId: number) {
    this.microServiceHttpService.delete<unknown>('whitelabel', `chatbot/bot/${botId}`, {
      showLoader: true,
      showToast: true,
      successMessage: 'Chatbot deleted successfully',
    }).subscribe({
      next: () => {
        this.getChatbotsApi();
      },
      error: () => {
        toast.error('Failed to delete chatbot, please try again later.');
      },
    });
  }
}
